/**
 * src/components/agent/DictationWaveform.jsx
 *
 * Tiny 5-bar live waveform driven by `audioLevel` (0..1 from useDictation).
 * Rendered inline in the composer while dictation is active so the user
 * has an unambiguous "you're being heard" signal. Purely presentational —
 * no state, no refs, no effects. The parent drives the level.
 *
 * Visual: 5 equal-width vertical bars, each height derived from a phase-
 * shifted multiplier of `audioLevel`. Even at level=0 the bars pulse at a
 * low baseline so the indicator never looks broken.
 */

const BAR_COUNT = 5;
const BAR_WIDTH = 3;
const BAR_GAP = 3;
const MAX_HEIGHT = 20;
const MIN_HEIGHT = 4;

// Phase offsets per bar so the waveform looks like a simple live spectrum
// rather than N identical bars. Pure cosine gives a nice hump shape.
const PHASE = [0.9, 1.15, 1.3, 1.15, 0.9];

export default function DictationWaveform({ level = 0, color = 'var(--bad)', ariaLabel = 'Listening' }) {
  const clamped = Math.max(0, Math.min(1, Number(level) || 0));
  const totalWidth = BAR_COUNT * BAR_WIDTH + (BAR_COUNT - 1) * BAR_GAP;

  return (
    <div
      data-testid="dictation-waveform"
      role="img"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: BAR_GAP,
        height: MAX_HEIGHT,
        width: totalWidth,
      }}
    >
      {Array.from({ length: BAR_COUNT }, (_, i) => {
        const phase = PHASE[i] ?? 1;
        const h = Math.max(MIN_HEIGHT, Math.round(MAX_HEIGHT * clamped * phase));
        return (
          <span
            key={i}
            data-testid={`dictation-waveform-bar-${i}`}
            style={{
              display: 'inline-block',
              width: BAR_WIDTH,
              height: h,
              background: color,
              borderRadius: BAR_WIDTH / 2,
              transition: 'height 60ms linear',
            }}
          />
        );
      })}
    </div>
  );
}
